/**
 * Template Library page (templates slice 7; sector suites 2026-09-09): the
 * human-facing shelf for the Edinshall-curated audit-template and
 * finding-template packs. Browse by sector and jurisdiction, inspect
 * counts and references, and download the JSON pack — or import it
 * directly inside Edinshall Audit via Template Studio → "Browse library".
 * The portal serves this content verbatim; it never computes template
 * content (owner decision 2026-07-05). The library disclaimer is shown
 * above the shelf on every visit.
 */

import React, { useEffect, useMemo, useState } from 'react';

interface CatalogueEntry {
  kind: 'audit' | 'finding';
  templateKey: string;
  title: string;
  description?: string;
  sector: string;
  jurisdiction: string;
  references?: string[];
  tags?: string[];
  sectionCount: number;
  itemCount: number;
  findingTemplateKeys?: string[];
  requiresEvidence?: boolean;
  findingType?: string;
}

interface CatalogueResponse {
  disclaimer?: string;
  sectors?: Array<{ key: string; label: string }>;
  jurisdictions?: Array<{ key: string; label: string }>;
  packs?: CatalogueEntry[];
  findingPacks?: CatalogueEntry[];
}

const ALL = '__all__';

const TemplateLibrary = (): JSX.Element => {
  const [catalogue, setCatalogue] = useState<CatalogueResponse>({});
  const [error, setError] = useState<string>();
  const [loading, setLoading] = useState(true);
  const [sector, setSector] = useState(ALL);
  const [jurisdiction, setJurisdiction] = useState(ALL);

  useEffect(() => {
    const load = async (): Promise<void> => {
      try {
        const response = await fetch('/api/template-library');
        if (!response.ok) {
          throw new Error(`Catalogue request failed (${response.status})`);
        }
        setCatalogue((await response.json()) as CatalogueResponse);
      } catch (err) {
        setError((err as Error).message);
      } finally {
        setLoading(false);
      }
    };
    void load();
  }, []);

  const labelFor = (list: Array<{ key: string; label: string }> | undefined, key: string): string =>
    list?.find(entry => entry.key === key)?.label ?? key;

  const matches = (entry: CatalogueEntry): boolean =>
    (sector === ALL || entry.sector === sector) && (jurisdiction === ALL || entry.jurisdiction === jurisdiction);

  const visiblePacks = useMemo(() => (catalogue.packs ?? []).filter(matches), [catalogue.packs, sector, jurisdiction]);
  const visibleFindingPacks = useMemo(() => {
    const related = new Set(visiblePacks.flatMap(pack => pack.findingTemplateKeys ?? []));
    return (catalogue.findingPacks ?? []).filter(pack => matches(pack) || related.has(pack.templateKey));
  }, [catalogue.findingPacks, visiblePacks, sector, jurisdiction]);
  const findingTitle = (key: string): string => catalogue.findingPacks?.find(pack => pack.templateKey === key)?.title ?? key;

  return (
    <div>
      <div className="card">
        <h1 className="section-title">Template Library</h1>
        <p>
          Curated audit-template and finding-template packs maintained by Edinshall for manufacturing and for
          financial services in the United Kingdom, United States, France, Canada, Spain, the Netherlands and the
          EU. Download a pack as JSON, or import it directly inside Edinshall Audit: open <strong>Templates</strong>{' '}
          on the dashboard and use <strong>Browse library</strong> (template builders). Imported packs arrive as
          drafts your team adapts and publishes.
        </p>
        {catalogue.disclaimer && (
          <p className="badge-subtle" role="note" aria-label="Template library disclaimer" data-testid="template-library-disclaimer">
            <strong>Please note:</strong> {catalogue.disclaimer}
          </p>
        )}
      </div>

      {loading && <div className="card">Loading the template library…</div>}
      {error && <div className="card">The template library could not be loaded: {error}</div>}

      {!loading && !error && (
        <>
          <div className="card">
            <div className="flex-row" style={{ gap: 16, flexWrap: 'wrap' }}>
              <label>
                Sector{' '}
                <select value={sector} onChange={event => setSector(event.target.value)} aria-label="Filter by sector">
                  <option value={ALL}>All sectors</option>
                  {(catalogue.sectors ?? []).map(entry => (
                    <option key={entry.key} value={entry.key}>{entry.label}</option>
                  ))}
                </select>
              </label>
              <label>
                Jurisdiction{' '}
                <select value={jurisdiction} onChange={event => setJurisdiction(event.target.value)} aria-label="Filter by jurisdiction">
                  <option value={ALL}>All jurisdictions</option>
                  {(catalogue.jurisdictions ?? []).map(entry => (
                    <option key={entry.key} value={entry.key}>{entry.label}</option>
                  ))}
                </select>
              </label>
              <span className="meta">
                {visiblePacks.length} audit templates · {visibleFindingPacks.length} finding templates
              </span>
            </div>
          </div>

          <h2 className="section-title">Audit templates</h2>
          {visiblePacks.length === 0 && <div className="card">No audit templates match the selected filters.</div>}
          {visiblePacks.map(pack => (
            <div className="card" key={pack.templateKey} data-testid="template-library-pack">
              <h3>{pack.title}</h3>
              <p className="meta">
                {labelFor(catalogue.sectors, pack.sector)} · {labelFor(catalogue.jurisdictions, pack.jurisdiction)}
                {pack.tags?.length ? ` · ${pack.tags.join(', ')}` : ''}
              </p>
              {pack.description && <p>{pack.description}</p>}
              {pack.references?.length ? (
                <p className="meta">Informed by: {pack.references.join('; ')}</p>
              ) : null}
              <p>
                {pack.itemCount} checks{pack.sectionCount > 0 ? ` across ${pack.sectionCount} sections` : ' (flat checklist)'}
                {pack.findingTemplateKeys?.length
                  ? ` · offers ${pack.findingTemplateKeys.length} finding templates: ${pack.findingTemplateKeys.map(findingTitle).join(', ')}`
                  : ''}
              </p>
              <a
                className="button"
                href={`/api/template-library/${encodeURIComponent(pack.templateKey)}`}
                download={`${pack.templateKey}.edinshall-template.json`}
              >
                Download JSON pack
              </a>
            </div>
          ))}

          <h2 className="section-title">Finding templates</h2>
          <p className="meta">
            Types of finding an audit programme records, with default severity, guidance, evidence rule and typed
            fields. Audit-template packs offer these by key; import the finding templates first so the offering
            resolves when the audit template is published.
          </p>
          {visibleFindingPacks.length === 0 && <div className="card">No finding templates match the selected filters.</div>}
          {visibleFindingPacks.map(pack => (
            <div className="card" key={pack.templateKey} data-testid="template-library-finding-pack">
              <h3>{pack.title}</h3>
              <p className="meta">
                {labelFor(catalogue.sectors, pack.sector)} · {labelFor(catalogue.jurisdictions, pack.jurisdiction)}
                {pack.findingType ? ` · ${pack.findingType}` : ''}
                {pack.requiresEvidence ? ' · evidence required' : ''}
              </p>
              {pack.description && <p>{pack.description}</p>}
              <p>{pack.itemCount} typed fields</p>
              <a
                className="button"
                href={`/api/template-library/finding/${encodeURIComponent(pack.templateKey)}`}
                download={`${pack.templateKey}.edinshall-finding-template.json`}
              >
                Download JSON pack
              </a>
            </div>
          ))}
        </>
      )}
    </div>
  );
};

export default TemplateLibrary;
